import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { EScheduleService } from "@store/new-e-schedule/new-e-schedule.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import {
  CISelectedPolicyParams,
  GET_CI_AGENT_LIST,
  GET_CI_PRODUCT_LIST,
  GET_CI_REPORT,
  GET_CI_TEMPLATE,
  GET_MASTER_POLICY_LIST,
  UPDATE_SELECTED_POLICY,
  UPDATE_UPLOAD_CI_STATUS,
  UPLOAD_CI_TEMPLATE,
} from "./certificate-of-insurance.action";
import { CIReportQuery, CIService } from "./certificate-of-insurance.service";

export interface CIAgent {
  agentCode: string;
  code: string;
  branchCode: string;
  name: string;
  status: string;
  bancaAgent: boolean;
  sundriesAgent: boolean;
  superiorCode: string;
}

export interface CIProduct {
  code: string;
  descp: string;
  type: string;
  ciUploadInd: boolean;
}

export interface CIMasterPolicy {
  masterPolicyNo: string;
  effectiveDate: string;
  expiryDate: string;
  policyHolderName: string;
}

export interface CIModel {
  agentList: CIAgent[];
  productList: CIProduct[];
  masterPolicyList: CIMasterPolicy[] | null;
  uploadErrorMsg: string | null;
  uploadMessage: string | null;
  uploadStatus: boolean | null;
  ciBatchId: number | null;
  selectedMasterPolicy: string | CISelectedPolicyParams;
  eInvoiceConChangeInd: boolean;
}

interface CIUploadResponse {
  error?: string;
  message?: string;
  data: {
    ciBatchId: number;
    eInvoiceConChangeInd?: boolean;
  };
}

@State<CIModel>({
  name: "CIState",
  defaults: {
    agentList: [],
    productList: [],
    masterPolicyList: [],
    uploadErrorMsg: null,
    uploadMessage: null,
    uploadStatus: false,
    ciBatchId: null,
    selectedMasterPolicy: "",
    eInvoiceConChangeInd: false,
  },
})
@Injectable()
export class CIState {
  constructor(
    private readonly ciService: CIService,
    private readonly store: Store,
    private readonly escheduleService: EScheduleService,
  ) {}

  @Selector()
  public static getState(state: CIModel): CIModel {
    return state;
  }

  @Selector()
  public static agentList(state: CIModel): CIAgent[] {
    return state.agentList ?? [];
  }

  @Selector()
  public static productList(state: CIModel): CIProduct[] {
    return state.productList ?? [];
  }

  @Selector()
  public static masterPolicyList(state: CIModel): CIMasterPolicy[] {
    return state.masterPolicyList ?? [];
  }

  @Selector()
  public static selectedMasterPolicy(
    state: CIModel,
  ): string | CISelectedPolicyParams {
    return state.selectedMasterPolicy;
  }

  @Action(GET_CI_AGENT_LIST)
  public getAgentList(
    { patchState }: StateContext<CIModel>,
    { payload }: GET_CI_AGENT_LIST,
  ): Observable<void> {
    return this.escheduleService.getAgentList(payload).pipe(
      map((res) => {
        if (res?.data) {
          const agentList = res.data?.agentList.filter(
            (agent) => agent.agentCode !== "-1",
          );
          patchState({
            agentList: agentList,
          });
        }
      }),
    );
  }

  @Action(GET_CI_PRODUCT_LIST)
  public getProductList(
    { patchState }: StateContext<CIModel>,
    { payload }: GET_CI_PRODUCT_LIST,
  ): Observable<void> {
    return this.escheduleService.getProductList(payload).pipe(
      map((res) => {
        const ciProductList = res.data?.productList
          ? res.data.productList.filter((data) => data.ciUploadInd)
          : [];
        patchState({
          productList: ciProductList,
        });
      }),
    );
  }

  @Action(GET_MASTER_POLICY_LIST)
  public getMasterPolicyList(
    { patchState }: StateContext<CIModel>,
    { payload }: GET_MASTER_POLICY_LIST,
  ): Observable<void> {
    return this.ciService
      .getMasterPolicyList(payload.agentCode, payload.productCode)
      .pipe(
        map((res) => {
          patchState({
            masterPolicyList: res ?? null,
          });
        }),
      );
  }

  @Action(UPLOAD_CI_TEMPLATE)
  public uploadCITemplate(
    { patchState }: StateContext<CIModel>,
    { payload }: UPLOAD_CI_TEMPLATE,
  ): Observable<void> {
    return new Observable<void>((observer) => {
      const params = this.store.selectSnapshot(CIState?.selectedMasterPolicy);
      const formData = new FormData();
      formData.append("file", payload.raw as File);
      formData.append(
        "request",
        new Blob([JSON.stringify(params)], { type: "application/json" }),
      );
      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }
        if (xhr.status === 200) {
          const res = JSON.parse(xhr.response) as CIUploadResponse;
          observer.next();
          if (!res || res["error"]) {
            patchState({
              uploadStatus: false,
              uploadErrorMsg: res.error,
            });
          } else if (res.message) {
            patchState({
              uploadStatus: true,
              uploadMessage: res.message,
              ciBatchId: res.data.ciBatchId,
              eInvoiceConChangeInd: res.data.eInvoiceConChangeInd
                ? res.data.eInvoiceConChangeInd
                : false,
            });
          } else {
            patchState({
              uploadStatus: false,
              uploadMessage: null,
              uploadErrorMsg: null,
            });
          }
        }
        observer.complete();
      };
      xhr.open("POST", this.ciService.uploadCiInfo(), true);
      xhr.send(formData);
    });
  }

  @Action(UPDATE_SELECTED_POLICY)
  public updateSelectedPolicy(
    { patchState }: StateContext<CIModel>,
    { payload }: UPDATE_SELECTED_POLICY,
  ): void {
    patchState({
      selectedMasterPolicy: payload,
    });
  }

  @Action(UPDATE_UPLOAD_CI_STATUS)
  public updateUploadCIStatus(
    { patchState }: StateContext<CIModel>,
    { payload: _payload }: UPDATE_UPLOAD_CI_STATUS,
  ): void {
    patchState({
      uploadStatus: null,
      uploadMessage: null,
      uploadErrorMsg: null,
    });
  }

  @Action(GET_CI_TEMPLATE)
  public getCiTemplate(
    _ctx: StateContext<CIModel>,
    { payload }: GET_CI_TEMPLATE,
  ): Window | null {
    return this.ciService.downloadCITemplate(
      payload.fileName,
      payload.productType,
    );
  }

  @Action(GET_CI_REPORT)
  public getCIReport(
    _ctx: StateContext<CIModel>,
    { payload }: GET_CI_REPORT,
  ): Window | null {
    return this.ciService.getCIReport(payload as CIReportQuery);
  }
}
